import { SpanContext, SpanStatusCode } from '@opentelemetry/api';
import { AxiosRequestConfig } from 'axios';
import { zip } from 'lodash';
import { batch } from 'react-redux';
import { emeraldRequest } from 'src/common/api/emerald-request';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { EmeraldRequestParams } from 'src/common/interfaces/requests/EmeraldRequestParams';
import { DeviceStatusActions } from 'src/common/redux/reducers/device-status-reducer';
import { ScaleActions } from 'src/common/redux/reducers/scale-reducer';
import { showDeviceAlertDialog } from 'src/device-status/device-status';
import { IDeviceConfigurationDictionary } from 'src/device-status/interfaces/IDeviceConfigurationDictionary';
import { getMetaData } from 'src/xlr-configuration/pos-service-type-utils';
import { axiosRequest } from '../common/api/axios-request';
import { getDeviceType } from '../common/device/initialize-devices';
import { DeviceType } from '../common/enumerations/device-type';
import { BASE_RDS, DEFAULT_RDS_PORT } from '../common/helpers/config';
import XLRLogger from '../common/helpers/logger';
import { IReceipt } from '../common/interfaces/dtos/IReceipt';
import { store } from '../common/redux/configure-store';
import { addDevices } from '../device-status/business-logic/add-device';
import { handleCashDrawerMessage } from './cash-drawer-operations';
import { RdsDeviceMethods, RdsResponseType } from './device-enums';
import { handleMICRMessage } from './micr-operations';
import { handlePrinterMessage } from './printer-operations';
import { handleScaleMessage } from './scale-operations';
import { handleScannerMessage, initializeScanner } from './scanner-operations';
import { JposConst } from './jpos-const';
import {
  showDeviceErrorMessageModal,
  showDeviceUnhealthyStatusMessageModal,
} from '../device-message/device-message';
import { IDeviceStatusItem } from 'src/common/interfaces/states/IDeviceStatusItem';
import { HealthyStatus } from './jpos-healthy-status';

const loggerSource = 'devices/rds-connection.ts';
let ws: WebSocket;

export const initializeSocketConnection = async (parentSpanContext?: SpanContext) => {
  if (ws === undefined || ws.readyState !== WebSocket.OPEN) {
    await connect(parentSpanContext);
  }
};

export const initializePeripheralConnection = async (
  deviceList: IDeviceConfigurationDictionary[],
  parentSpanContext?: SpanContext,
): Promise<void> => {
  if (!deviceList.length) {
    return;
  }

  await initializeSocketConnection(parentSpanContext);

  const devices = deviceList.map(device => ({
    type: getDeviceType(device.Type),
    logicalName: device.LogicalName,
  }));

  await initializeDevices(devices)
    .then(res => {
      XLRLogger.info({
        message: 'initialize devices resolved',
        source: 'rds-connection.ts:initializeDevices',
        data: res || {},
      });
      batch(() => {
        zip(devices, res).forEach(([device, response]) => {
          if (!device || !response) {
            return;
          }
          const { type } = device;
          const logicalName = device.logicalName;
          store.dispatch(
            DeviceStatusActions.updateDevice({
              logicalName,
              warning: !response.success,
              initialized: response.success,
              deviceType: type,
              deviceStatus: HealthyStatus.JDEVICE_ONLINE,
            }),
          );
          if (type === DeviceType.Scanner) {
            initializeScanner(logicalName, false, false);
          }
          if (type === DeviceType.Scale) {
            const { weightUnit, maxWeight } = response.metaDataMap;
            if (weightUnit) {
              store.dispatch(ScaleActions.setWeightUnit(weightUnit));
            }
            if (maxWeight) {
              store.dispatch(ScaleActions.setMaxWeight(+maxWeight));
            }
          }
        });
      });
      return res as DeviceResponse[];
    })
    .catch(reason => {
      XLRLogger.debug({
        message: 'Could not initialize devices',
        source: 'rds-connection.ts',
        data: reason,
      });
      throw new Error('Could not initialize devices');
    });

  if (devices.find(d => d.type === DeviceType.Printer)) {
    await getBitmap(parentSpanContext)
      .then(async bitmap => {
        await postBitmapToRds(bitmap);
      })
      .catch(error => {
        XLRLogger.debug({
          message: 'Could not find bitmap',
          source: 'rds-connection.ts',
          data: error,
        });
      });
  }
};

const initializeDevices = (devices): Promise<DeviceResponse[]> => {
  return rdsRequest({
    url: `/rds/initialize`,
    method: 'POST',
    data: {
      devices,
    },
  }) as Promise<DeviceResponse[]>;
};

const getBitmap = async (parentSpanContext?: SpanContext): Promise<string> => {
  const axiosConfig: AxiosRequestConfig = {
    url: '/receipt-management/image-lookup',
    method: 'POST',
    data: {
      touchPointGroupId: store.getState().touchPoint.touchPointConfiguration.touchPointGroupId,
      businessUnitId: store.getState().touchPoint.touchPointConfiguration.storeId,
    },
  };
  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: false,
    showLoggingInfo: true,
    spanOptions: {
      name: 'receipt-image-lookup',
      parentContext: parentSpanContext,
    },
  };
  return emeraldRequest<string>(emeraldRequestParams);
};

const postBitmapToRds = (bitmap: string): Promise<DeviceResponse> => {
  const postBitmapToRdsConfig: AxiosRequestConfig = {
    url: `/rds/store-logo`,
    method: 'POST',
    data: {
      bitmap,
    },
  };
  return rdsRequest(postBitmapToRdsConfig) as Promise<DeviceResponse>;
};

// debating if this provides enough readability to be worth it in the long run
const invalidateScale = (): void => {
  store.dispatch(ScaleActions.resetState());
};

export const rdsRequest = async (request: AxiosRequestConfig): Promise<unknown> => {
  const { RDS_PORT = DEFAULT_RDS_PORT, RDS_HOSTNAME = BASE_RDS } = getMetaData().xlrConfig;
  return await axiosRequest({
    ...request,
    url: `http://${RDS_HOSTNAME}:${RDS_PORT}${request.url}`,
  })
    .then(response => {
      return response.data;
    })
    .catch(err => {
      XLRLogger.error({ message: err.reason });
      return {};
    });
};

export const sendPeripheralMessage = (
  data: PeripheralMessageData,
  url: string,
  method?: 'POST' | 'GET',
): Promise<DeviceResponse> => {
  const _method = method || 'POST';

  XLRLogger.debug({
    message: 'RDS REST Request: ',
    source: loggerSource,
    data: [data, url, method],
  });

  return rdsRequest({
    url,
    method: _method,
    data,
  }) as Promise<DeviceResponse>;
};

function setDeviceData(deviceData?: IDeviceStatusItem, status?: string): void {
  if (deviceData) {
    const data = {
      ...deviceData,
      deviceStatus: status ? status : HealthyStatus.JDEVICE_ONLINE,
    };
    store.dispatch(DeviceStatusActions.updateDevice(data));
  }
}

// exported for simpler cypress testing with device messages
export const onDeviceMessage = (jsonMessage: { data: string }): void => {
  const { deviceType, data }: RdsPeripheralPayload<DeviceResponse> = JSON.parse(jsonMessage.data);
  if (data.responseType === RdsResponseType.ERROR) {
    handleErrorMessage(data);
  }
  if (data.responseType === RdsResponseType.STATUS_UPDATE && data.metaDataMap.statusMessage) {
    if (deviceType === DeviceType.Scale) {
      const scaleData = store
        .getState()
        .deviceStatus.devices.find(i => i.deviceType === DeviceType.Scale);
      setDeviceData(scaleData, data.metaDataMap.statusMessage);
    }
    const deviceData = store
      .getState()
      .deviceStatus.devices.find(i => i.logicalName === data.metaDataMap?.logicalName);
    setDeviceData(deviceData, data.metaDataMap.statusMessage);
    updateDeviceHealth(data, false);
    if (isUnhealthy(data.metaDataMap.statusMessage)) {
      handleUnhealthyStatusMessage(data);
    }
  }

  switch (deviceType) {
    case DeviceType.CashDrawer:
      handleCashDrawerMessage(data);
      break;
    case DeviceType.Scanner:
      handleScannerMessage(data);
      break;
    case DeviceType.Printer:
      handlePrinterMessage(data);
      break;
    case DeviceType.Scale:
      handleScaleMessage(data);
      break;
    case DeviceType.MICR:
      handleMICRMessage(data);
      break;
    default:
      handleDeviceMessage(data);
  }
};

const connect = (parentSpanContext?: SpanContext): Promise<void> => {
  return new Promise((resolve, reject) => {
    const wsSpan = createSpan('[RDS] Connect WebSocket', parentSpanContext, true);
    const { RDS_PORT = DEFAULT_RDS_PORT, RDS_HOSTNAME = BASE_RDS } = getMetaData().xlrConfig;
    let connected = false;
    ws = new WebSocket(`ws://${RDS_HOSTNAME}:${RDS_PORT}/connect`);
    ws.onmessage = onDeviceMessage;
    ws.onopen = function() {
      connected = true;
      endSpan(wsSpan);
      resolve();
    };
    ws.onclose = function() {
      if (connected) {
        XLRLogger.info({
          message: 'RDS WS connection closed!',
          source: loggerSource,
        });

        const endWsSpan = createSpan('[RDS] disconnect-ws');
        disconnect();
        endSpan(endWsSpan);
      }
    };
    ws.onerror = function(err) {
      wsSpan.setStatus({
        code: SpanStatusCode.ERROR,
        message: 'Connect WebSocket error',
      });
      endSpan(wsSpan);
      XLRLogger.error({
        message: 'RDS WS Error',
        source: loggerSource,
        data: err,
      });
      reject(err);
    };
  });
};

const disconnect = (): void => {
  invalidateScale();
  const deviceList = store.getState().touchPoint.touchPointConfiguration.deviceList;
  if (deviceList) {
    const hasCustomerDisplay = deviceList?.find(d => d.LogicalName === 'CustomerDisplay');
    let showDisplay = false;
    if (hasCustomerDisplay !== undefined) {
      showDisplay = true;
    }
    addDevices(deviceList, showDisplay);
  }
  showDeviceAlertDialog();
};

const handleDeviceMessage = (data: DeviceResponse): void => {
  XLRLogger.debug({
    source: loggerSource,
    message: `Received a message without a matching deviceType`,
    data: data,
  });
};

const handleUnhealthyStatusMessage = (data: DeviceResponse): void => {
  updateDeviceHealth(data, true);
  showDeviceUnhealthyStatusMessageModal({ data });
};
const updateDeviceHealth = (data: DeviceResponse, warning: boolean): void => {
  if (data.metaDataMap.logicalName) {
    const { updateDevice } = DeviceStatusActions;
    const { logicalName } = data.metaDataMap;
    const { devices } = store.getState().deviceStatus;
    const deviceToUpdate = devices.find(d => d.logicalName === logicalName);
    if (deviceToUpdate?.warning !== warning) {
      // TODO check for 2 in 1 devices.
      store.dispatch(updateDevice({ ...deviceToUpdate, warning: warning }));
    }
  }
};

const handleErrorMessage = (data: DeviceResponse): void => {
  updateDeviceHealth(data, false);
  showDeviceErrorMessageModal({ data });
};

export const isUnhealthy = (code: string): boolean => {
  return [
    JposConst.JPOS_SUE_POWER_OFF,
    JposConst.JPOS_SUE_POWER_OFFLINE,
    JposConst.JPOS_SUE_POWER_OFF_OFFLINE,
    // JposConst.PTR_SUE_JRN_EMPTY,
    // JposConst.PTR_SUE_JRN_NEAREMPTY,
    // JposConst.PTR_SUE_REC_EMPTY,
    // JposConst.PTR_SUE_REC_NEAREMPTY,
    // JposConst.PTR_SUE_SLP_EMPTY,
    // JposConst.PTR_SUE_SLP_NEAREMPTY,
    // JposConst.PTR_SUE_JRN_CARTRIDGE_EMPTY,
    // JposConst.PTR_SUE_JRN_CARTRIDGE_NEAREMPTY,
    // JposConst.PTR_SUE_REC_CARTRIDGE_EMPTY,
    // JposConst.PTR_SUE_REC_CARTRIDGE_NEAREMPTY,
    // JposConst.PTR_SUE_SLP_CARTRIDGE_EMPTY,
    // JposConst.PTR_SUE_SLP_CARTRIDGE_NEAREMPTY,
    // JposConst.SCL_SUE_NOT_READY,
    // JposConst.PTR_SUE_COVER_OPEN,
    // JposConst.PTR_SUE_JRN_COVER_OPEN,
    // JposConst.PTR_SUE_REC_COVER_OPEN,
    // JposConst.PTR_SUE_SLP_COVER_OPEN,
  ].includes(JposConst[code]);
};

type PeripheralMessageData = DeviceRequest | RdsPeripheralPayload<PayloadData>;

export interface RdsPeripheralPayload<T> {
  deviceType: string;
  data: T;
}

interface PayloadData {
  actionType: RdsDeviceMethods;
  logicalName?: string;
  oPosReceiptData?: IReceipt;
  bitmap?: string;
}

interface DeviceRequest {
  type: DeviceType;
  logicalName: string;
}

export interface DeviceResponse {
  success: boolean;
  message?: string;
  error?: string;
  responseType?: RdsResponseType;
  metaDataMap: { [key: string]: string | undefined };
}

export interface ErrorMap extends MetaDataMap {
  errorCode: string;
  errorCodeMessage: string;
  extendedErrorCode?: string;
  extendedErrorCodeMessage?: string;
  errorLocus: string;
  errorResponse: string;
}

export interface StatusUpdateMap extends MetaDataMap {
  status: string;
  statusMessage: string;
}

export interface ScaleInitializationMap extends MetaDataMap {
  weightUnit: string;
  maxWeight: string;
}

export interface MetaDataMap {
  logicalName: string;
  sequenceNumber: string;
  when: string;
}

export interface MapType {
  [key: string]: string | undefined;
}
